import { useQuery } from "@tanstack/react-query";
import { Users, Video, CreditCard, TrendingUp, Zap, Shield } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/Card.tsx";
import { Spinner } from "@/components/ui/Spinner.tsx";
import { adminApi } from "@/api/admin.ts";

export function AdminDashboardPage() {
  const { data, isLoading } = useQuery({
    queryKey: ["admin", "stats"],
    queryFn: () => adminApi.stats(),
    refetchInterval: 30_000,
  });

  if (isLoading || !data) {
    return <div className="flex h-full items-center justify-center"><Spinner size="lg" /></div>;
  }

  const { totals, videosByStatus, usersByPlan } = data;

  return (
    <div className="mx-auto max-w-6xl p-8">
      <h1 className="mb-2 text-3xl font-bold text-fg">Admin Dashboard</h1>
      <p className="mb-8 text-fgMuted">Platform overview and key metrics</p>

      {/* Stat cards */}
      <div className="mb-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {[
          { icon: Users, label: "Total Users", value: totals.users, accent: "text-brand-500 bg-brand-500/10" },
          { icon: Video, label: "Total Videos", value: totals.videos, accent: "text-success bg-success/10" },
          { icon: CreditCard, label: "Active Subs", value: totals.activeSubscriptions, accent: "text-warning bg-warning/10" },
          { icon: TrendingUp, label: "Signups (7d)", value: totals.recentSignups, accent: "text-danger bg-danger/10" },
        ].map((stat) => {
          const Icon = stat.icon;
          return (
            <Card key={stat.label}>
              <CardContent className="flex items-center gap-4 py-5">
                <div className={`flex h-12 w-12 items-center justify-center rounded-xl ${stat.accent}`}>
                  <Icon className="h-6 w-6" />
                </div>
                <div>
                  <p className="text-3xl font-bold text-fg">{stat.value.toLocaleString()}</p>
                  <p className="text-sm text-fgMuted">{stat.label}</p>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Users by plan */}
        <Card>
          <CardHeader><CardTitle>Users by Plan</CardTitle></CardHeader>
          <CardContent>
            <div className="flex flex-col gap-3">
              {usersByPlan.map((row) => {
                const pct = totals.users > 0 ? Math.round((row.count / totals.users) * 100) : 0;
                return (
                  <div key={row.plan} className="flex items-center gap-3">
                    <span className="w-20 text-sm font-medium text-fg capitalize">{row.plan}</span>
                    <div className="flex-1 h-3 rounded-full bg-surface2 overflow-hidden">
                      <div className="h-full rounded-full bg-brand-500 transition-all" style={{ width: `${pct}%` }} />
                    </div>
                    <span className="w-16 text-right text-sm text-fgMuted">{row.count} ({pct}%)</span>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Videos by status */}
        <Card>
          <CardHeader><CardTitle>Videos by Status</CardTitle></CardHeader>
          <CardContent>
            <div className="flex flex-col gap-2">
              {videosByStatus.map((row) => (
                <div key={row.status} className="flex items-center justify-between rounded-lg bg-surface2/30 px-4 py-2.5">
                  <span className="text-sm font-medium text-fg capitalize">{row.status.replace(/_/g, " ")}</span>
                  <span className="text-sm font-bold text-fg">{row.count.toLocaleString()}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quick info */}
      <div className="mt-6 grid gap-4 sm:grid-cols-2">
        <Card>
          <CardContent className="flex items-center gap-4 py-5">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-success/10 text-success">
              <Zap className="h-5 w-5" />
            </div>
            <div>
              <p className="text-lg font-bold text-fg">{totals.activeGateways}</p>
              <p className="text-sm text-fgMuted">Active Payment Gateways</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="flex items-center gap-4 py-5">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-warning/10 text-warning">
              <Shield className="h-5 w-5" />
            </div>
            <div>
              <p className="text-lg font-bold text-fg">{Math.abs(totals.totalCreditsIssued).toLocaleString()}</p>
              <p className="text-sm text-fgMuted">Total Credits Circulated</p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
